import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, CalendarDays, Tent, Dog, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const blank = { title: "", client_id: "", dog: "", date: "", time: "", end_date: "", camping: false, facilities: "", notes: "", status: "planned" };

const statusColor = {
  planned: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
};

const STATUSES = ["planned", "completed", "cancelled"];

export default function EventsTab() {
  const [events, setEvents] = useState(null);
  const [clients, setClients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const load = () => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
    api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  };
  useEffect(load, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "";

  const save = async () => {
    if (!form.title.trim() || !form.date) {
      toast.error("Title and date are required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/events/${editing.id}`, form);
        toast.success("Event updated");
      } else {
        await api.post("/events", form);
        toast.success("Event added to calendar");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save event", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ev) => {
    try {
      await api.delete(`/events/${ev.id}`);
      setEvents((prev) => prev.filter((x) => x.id !== ev.id));
      setDialogOpen(false);
      toast.success("Event deleted from the calendar");
    } catch (e) {
      toast.error("Could not delete", { description: formatApiError(e) });
    }
  };

  if (events === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="events-loading-spinner" />
      </div>
    );
  }

  const months = events.reduce((acc, ev) => {
    const key = ev.date ? ev.date.slice(0, 7) : "No date";
    (acc[key] = acc[key] || []).push(ev);
    return acc;
  }, {});
  const monthKeys = Object.keys(months).sort((a, b) => (a < b ? 1 : -1));

  return (
    <div data-testid="events-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-white/50">{events.length} event{events.length === 1 ? "" : "s"} in the diary</p>
        <Button onClick={() => { setEditing(null); setForm(blank); setDialogOpen(true); }} data-testid="admin-add-event-button" className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold">
          <Plus className="h-4 w-4" /> Add Event
        </Button>
      </div>

      {(() => {
        const monthLabel = viewMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
        const firstDow = (viewMonth.getDay() + 6) % 7;
        const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDow; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        while (cells.length % 7 !== 0) cells.push(null);
        const iso = (d) => `${viewMonth.getFullYear()}-${String(viewMonth.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        const eventsByDate = events.reduce((acc, ev) => {
          if (ev.date) (acc[ev.date] = acc[ev.date] || []).push(ev);
          return acc;
        }, {});
        const todayIso = new Date().toISOString().slice(0, 10);
        const chipCls = {
          planned: "bg-blue-500/20 text-blue-300 hover:bg-blue-500/40",
          completed: "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/40",
          cancelled: "bg-red-500/20 text-red-300 hover:bg-red-500/40",
        };
        const shiftMonth = (n) => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + n, 1));
        return (
          <div className="rounded-lg border border-brand-line bg-brand-surface p-5" data-testid="events-calendar">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <button onClick={() => shiftMonth(-1)} data-testid="calendar-prev" className="h-9 w-9 rounded-md border border-brand-line text-white/60 hover:text-white hover:border-brand-crimson/60 transition-colors" aria-label="Previous month">‹</button>
              <p className="font-display text-xl font-bold text-white" data-testid="calendar-month-label">{monthLabel}</p>
              <div className="flex items-center gap-2">
                <button onClick={() => { const d = new Date(); setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="calendar-today" className="h-9 px-4 rounded-md border border-brand-line font-eyebrow text-[11px] uppercase tracking-[0.15em] font-bold text-white/60 hover:text-white transition-colors">Today</button>
                <button onClick={() => shiftMonth(1)} data-testid="calendar-next" className="h-9 w-9 rounded-md border border-brand-line text-white/60 hover:text-white hover:border-brand-crimson/60 transition-colors" aria-label="Next month">›</button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d} className="text-center font-eyebrow text-[10px] uppercase tracking-widest text-white/40 py-1">{d}</div>
              ))}
              {cells.map((d, i) => {
                if (!d) return <div key={`empty-${i}`} className="min-h-[88px] rounded-md bg-brand-obsidian/40 border border-brand-line/40" />;
                const dateIso = iso(d);
                const evs = eventsByDate[dateIso] || [];
                const isToday = dateIso === todayIso;
                return (
                  <div key={dateIso} data-testid={`calendar-day-${dateIso}`} className={`min-h-[88px] rounded-md border p-1.5 transition-colors ${isToday ? "border-brand-crimson bg-brand-crimson/10" : "border-brand-line bg-brand-obsidian/40 hover:border-brand-crimson/40"}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] font-bold ${isToday ? "text-brand-crimson" : "text-white/50"}`}>{d}</span>
                      <button
                        onClick={() => { setEditing(null); setForm({ ...blank, date: dateIso }); setDialogOpen(true); }}
                        className="text-white/30 hover:text-brand-crimson text-sm leading-none font-bold transition-colors"
                        data-testid={`calendar-add-${dateIso}`}
                        aria-label={`Add event on ${dateIso}`}
                      >
                        +
                      </button>
                    </div>
                    <div className="mt-1 space-y-1">
                      {evs.slice(0, 2).map((ev) => (
                        <button
                          key={ev.id}
                          onClick={() => { setEditing(ev); setForm({ ...blank, ...ev }); setDialogOpen(true); }}
                          data-testid={`calendar-event-${ev.id}`}
                          className={`w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded truncate transition-colors ${chipCls[ev.status] || chipCls.planned}`}
                          title={ev.title}
                        >
                          {ev.title}
                        </button>
                      ))}
                      {evs.length > 2 && <p className="text-[9px] text-white/40 px-1">+{evs.length - 2} more</p>}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-white/40">Click the + on any day to add an event. Click an event chip to open, edit and add more information.</p>
          </div>
        );
      })()}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-brand-surface border-brand-line max-w-lg max-h-[85vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{editing ? "Edit Event" : "Add Event"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Event / Job Title *</Label>
              <Input data-testid="event-form-title" value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Summer Festival - North Gate sweeps" className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Client</Label>
              <select
                value={form.client_id}
                onChange={(e) => setField("client_id", e.target.value)}
                data-testid="event-form-client"
                className={`h-9 w-full appearance-none rounded-md border border-brand-line bg-brand-obsidian px-3 text-sm focus:outline-none ${form.client_id ? "text-white" : "text-white/50"}`}
              >
                <option value="">No client linked</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Dog</Label>
                <Input data-testid="event-form-dog" value={form.dog} onChange={(e) => setField("dog", e.target.value)} placeholder="Shadow" className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Time</Label>
                <Input data-testid="event-form-time" value={form.time} onChange={(e) => setField("time", e.target.value)} placeholder="14:00 - 22:00" className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Date *</Label>
                <Input data-testid="event-form-date" type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">End Date (multi-day)</Label>
                <Input data-testid="event-form-end-date" type="date" value={form.end_date} onChange={(e) => setField("end_date", e.target.value)} className="bg-brand-obsidian border-brand-line" />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6 rounded-md border border-brand-line bg-brand-obsidian p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-white/80">Camping overnight?</span>
                <Switch checked={!!form.camping} onCheckedChange={(v) => setField("camping", v)} data-testid="event-form-camping" />
              </div>
              <div className="flex-1 min-w-[180px] space-y-1.5">
                <Label className="text-white/70 text-xs">Facilities</Label>
                <Input data-testid="event-form-facilities" value={form.facilities} onChange={(e) => setField("facilities", e.target.value)} placeholder="Toilets, parking, water" className="bg-brand-obsidian border-brand-line" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Notes</Label>
              <Textarea data-testid="event-form-notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} placeholder="Briefing point, kit, contacts..." className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="flex gap-3">
              {editing && (
                <Button
                  onClick={() => remove(editing)}
                  data-testid="event-delete-button"
                  className="flex-1 bg-transparent border border-red-500/40 text-red-400 hover:bg-red-500/10 font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6"
                >
                  <Trash2 className="h-4 w-4" /> Delete Event
                </Button>
              )}
              <Button onClick={save} disabled={busy} data-testid="event-save-button" className="flex-1 bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6">
                {busy ? "Saving..." : editing ? "Save Changes" : "Add to Calendar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
